import { CreatorAction, CreatorActionType } from '@/types/types';
import { ProjectRenderer } from './ProjectRenderer';
import { Loader } from './Loader';
import { getFileSrc } from '@/utils/utilities';
import { RenderUtils } from './RenderUtils';
import { MapType } from '@/api/AssetApi';
import { AssetsLib } from './AssetsLib';
import { SofaModule } from './objects/SofaModule';
import * as THREE from 'three';

export class ActionHandler {
	private loader = Loader.getInstance();
	private assetsLib = AssetsLib.getInstance();

	constructor(private renderer: ProjectRenderer) {
		this.renderer = renderer;
	}

	async dispatchAction(action: CreatorAction, target?: SofaModule) {
		switch (action.type) {
			case CreatorActionType.SET_MATERIAL:
				await this.handleMaterialAction(action, target);
				break;
			default:
				throw new Error('UNKNOWN_ACTION_TYPE');
		}
	}

	private async handleMaterialAction(
		action: CreatorAction,
		targetModule?: SofaModule
	) {
		const asset = action.payload.asset;
		if (!asset || !action.payload.target) return;

		const objectTargetName = action.payload.target;

		if (targetModule) targetModule.materialActions[objectTargetName] = action;
		else
			this.renderer.moduleManager.sofaModulesMap.forEach((mod) => {
				mod.materialActions[objectTargetName] = action;
			});

		const textures = await Promise.all(
			Object.values(MapType).map((type) => {
				if (!asset.maps[type]) return null;

				const cached = this.assetsLib.texturesCache.get(asset.maps[type].path);
				if (cached) return cached;

				return this.loader
					.loadTexture(getFileSrc(asset.maps[type].path))
					.then((texture) => {
						this.assetsLib.texturesCache.set(asset.maps[type]!.path, texture);

						return texture;
					});
			})
		);

		const parent = targetModule ? targetModule.group : this.renderer.collection;

		parent.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;
			if (child.userData.target !== objectTargetName) return;

			const displacementScale = child.userData.displacement_scale ?? 0.0005;

			const cacheKey = `${asset.id}_${displacementScale}`;
			let material = this.assetsLib.materialCache.get(cacheKey);

			if (!material) {
				for (const texture of textures) {
					if (!texture) continue;

					texture.wrapS = THREE.RepeatWrapping;
					texture.wrapT = THREE.RepeatWrapping;
					texture.repeat.set(1, 1);
				}

				const [
					map,
					roughnessMap,
					normalMap,
					displacementMap,
					metalnessMap,
					aoMap,
					lightMap,
				] = textures;

				if (!map) throw new Error('MISSING_MAP');

				map.colorSpace = THREE.SRGBColorSpace;

				for (const texture of [
					roughnessMap,
					normalMap,
					displacementMap,
					metalnessMap,
					aoMap,
					lightMap,
				]) {
					if (!texture) continue;
					texture.colorSpace = THREE.LinearSRGBColorSpace;
				}

				material = new THREE.MeshStandardMaterial({
					map,
					normalScale: new THREE.Vector2(0.025, 0.025),
					displacementMap,
					displacementScale,
					metalnessMap,
					normalMap,
					roughnessMap,
					aoMap,
					lightMap,
					...asset.options,
				});

				this.assetsLib.materialCache.set(cacheKey, material);
			}

			child.material = material;
		});
	}
}
